import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import '../../styles/Admin.css';

const MEDUSA_BACKEND_URL = process.env.REACT_APP_MEDUSA_BACKEND_URL || 'http://localhost:9000';

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, complete, incomplete
  
  const { token, logout, isAuthenticated } = useAdmin();
  const navigate = useNavigate();

  const fetchProducts = useCallback(async () => {
    try {
      const response = await fetch(
        `${MEDUSA_BACKEND_URL}/admin/products?limit=100`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchProducts();
  }, [isAuthenticated, navigate, fetchProducts]);

  const isProductComplete = (product) => {
    const metadata = product.metadata || {};
    const requiredFields = [
      'benefits_label',
      'benefits_description',
      'active_ingredients',
      'how_to_use'
    ];
    return requiredFields.every(field => metadata[field] && metadata[field].trim() !== '');
  };

  const getCompletionPercentage = (product) => {
    const metadata = product.metadata || {};
    const fields = [
      'benefits_label',
      'benefits_description',
      'active_ingredients',
      'ingredients_detail',
      'how_to_use',
      'dosis',
      'aroma',
      'texture_image',
      'sinergia_ids',
      'recommended_ids'
    ];
    const completed = fields.filter(field => metadata[field] && metadata[field].toString().trim() !== '').length;
    return Math.round((completed / fields.length) * 100);
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.title.toLowerCase().includes(search.toLowerCase());
    
    if (filter === 'complete') {
      return matchesSearch && isProductComplete(product);
    } else if (filter === 'incomplete') {
      return matchesSearch && !isProductComplete(product);
    }
    return matchesSearch;
  });

  const completedCount = products.filter(isProductComplete).length;

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-loading">Cargando productos...</div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>Physiology Admin</h1>
        </div>
        <div className="admin-header-right">
          <button onClick={logout} className="admin-logout-btn">
            Cerrar sesión
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-content">
          {/* Page Title */}
          <div className="admin-page-header">
            <h2>Productos</h2>
            <p className="admin-stats">
              {completedCount} de {products.length} productos completos
            </p>
          </div>

          {/* Filters */}
          <div className="admin-filters">
            <div className="admin-search">
              <input
                type="text"
                placeholder="Buscar producto..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="admin-filter-buttons">
              <button 
                className={`admin-filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                Todos ({products.length})
              </button>
              <button 
                className={`admin-filter-btn ${filter === 'complete' ? 'active' : ''}`}
                onClick={() => setFilter('complete')}
              >
                Completos ({completedCount})
              </button>
              <button 
                className={`admin-filter-btn ${filter === 'incomplete' ? 'active' : ''}`}
                onClick={() => setFilter('incomplete')}
              >
                Incompletos ({products.length - completedCount})
              </button>
            </div>
          </div>

          {/* Products Grid */}
          <div className="admin-products-grid">
            {filteredProducts.map(product => (
              <Link 
                key={product.id} 
                to={`/admin/products/${product.id}`}
                className="admin-product-card"
              >
                <div className="admin-product-image">
                  {product.thumbnail ? (
                    <img src={product.thumbnail} alt={product.title} />
                  ) : (
                    <div className="admin-product-no-image">Sin imagen</div>
                  )}
                </div>
                <div className="admin-product-info">
                  <h3>{product.title}</h3>
                  <div className="admin-product-meta">
                    <span className={`admin-status ${isProductComplete(product) ? 'complete' : 'incomplete'}`}>
                      {isProductComplete(product) ? '✓ Completo' : 'Incompleto'}
                    </span>
                    <span className="admin-percentage">
                      {getCompletionPercentage(product)}%
                    </span>
                  </div>
                  <div className="admin-progress-bar">
                    <div 
                      className="admin-progress-fill"
                      style={{ width: `${getCompletionPercentage(product)}%` }}
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="admin-no-results">
              No se encontraron productos
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
